import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bot, LayoutDashboard, RefreshCw, Activity, Droplets, Thermometer, Sun, Wind, FlaskConical, Leaf, CloudRain, Cloud, MapPin, ThermometerSun, Waves } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LineChart, Line, AreaChart, Area, CartesianGrid, Legend, ReferenceArea } from 'recharts'; import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import 'leaflet/dist/leaflet.css';

import Card from '../components/ui/Card';
import SensorCard from '../components/ui/SensorCard';
import IoTMap from '../components/ui/IoTMap';
import MacroWeatherChart from '../components/MacroWeatherChart';
import GlobalContextBar from '../components/GlobalContextBar';
import DiagnosticsPanel from '../components/DiagnosticsPanel';
import ApiService from '../services/apiService';
import TranslationService from '../services/translationService';
import { commitModule, markRun } from '../utils/reportMarkers';
import { capDataUrl, dataUrlBytes, MAX_MAP_IMAGE_EDGE_PX, MAX_MAP_IMAGE_BYTES } from '../utils/imageGuards';
let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

// ==========================================================================
// REPORT PAYLOAD HELPERS  (Geonutria Report Contract v2)
// Pure module-scope helpers — no React state, safe to call from any handler.
// ==========================================================================

// Hard-coded 0 server-side: these can never be a real reading.
const STRUCTURALLY_DEAD = ['Light_Intensity', 'Chlorophyll_Content', 'Chlorophyll'];
// 0 is physically impossible for these -> treat 0 as "not installed".
//
// Ambient_Temperature and Humidity are included on the backend's own authority:
// services/iot_manager.py::_apply_weather_fallback_and_log tests `ambient_temp == 0
// or ambient_humidity == 0` and substitutes Open-Meteo values, i.e. the platform
// already defines 0 as "no reading" for exactly these two. A 0 that still reaches
// here means both the probe AND the weather fallback failed, so it is never a
// real measurement -- which is why the report used to print "Air Temp 0.0 °C".
// Soil_Moisture, Soil_Temperature and N/P/K are deliberately NOT listed: 0 is a
// genuine reading for those (bone-dry soil, freezing ground, depleted nutrient).
const ZERO_MEANS_ABSENT = ['Soil_pH', 'Electrochemical_Signal', 'Soil_Salinity',
                           'Salinity', 'TDS', 'Epsilon',
                           'Ambient_Temperature', 'Humidity'];

/**
 * Presence rule: a sensor that is not installed is ABSENT from iot_data entirely
 * (no key, no null, no 0). A genuine 0 is preserved for the sensors where 0 is a
 * physically meaningful reading (N/P/K, moisture, humidity, temperatures).
 */
const buildIotData = (sensors) => {
  const out = {};
  Object.entries(sensors || {}).forEach(([rawKey, v]) => {
    // canonical wire spelling for EC / salinity
    const key = (rawKey === 'Electrochemical_Signal' || rawKey === 'EC') ? 'Soil_Salinity' : rawKey;
    if (STRUCTURALLY_DEAD.includes(key)) return;          // never emit
    if (v === null || v === undefined) return;            // absent
    const n = Number(v);
    if (!Number.isFinite(n)) return;                      // absent
    if (n === 0 && ZERO_MEANS_ABSENT.includes(key)) return; // plausibility floor
    out[key] = n;
  });
  return out;
};

// ai_diagnosis.probabilities is keyed Healthy|Moderate|High while
// ai_diagnosis.status is Healthy|Moderate Stress|High Stress.
const PROB_KEY = { 'Healthy': 'Healthy', 'Moderate Stress': 'Moderate', 'High Stress': 'High' };
const readConfidence = (diag) => {
  const probs = diag?.probabilities;
  const status = diag?.status;
  const raw = probs?.[PROB_KEY[status]] ?? 0;
  const pct = Math.round(Number(raw) * 100);
  return Number.isFinite(pct) ? pct : 0;
};

// ── Historical readings ────────────────────────────────────────────────────
// Fixed priority order; light / chlorophyll are forbidden (hard-coded 0 server-side).
const HISTORY_METRICS = [
  { key: 'moisture', sensor: 'Soil_Moisture', unit: '%' },
  { key: 'soil_temp', sensor: 'Soil_Temperature', unit: '°C' },
  { key: 'temperature', sensor: 'Ambient_Temperature', unit: '°C' },
  { key: 'humidity', sensor: 'Humidity', unit: '%' },
  { key: 'ph', sensor: 'Soil_pH', unit: '' },
  { key: 'nitrogen', sensor: 'Nitrogen_Level', unit: 'mg/kg' },
  { key: 'phosphorus', sensor: 'Phosphorus_Level', unit: 'mg/kg' },
  { key: 'potassium', sensor: 'Potassium_Level', unit: 'mg/kg' }
];
const HISTORY_POINT_CAP = 24;

const pad2 = (n) => String(n).padStart(2, '0');

// Short pre-formatted ASCII display label (<= 16 chars). The backend never parses it.
const historyPointLabel = (rawTs, interval) => {
  const d = new Date(rawTs);
  if (isNaN(d.getTime())) return String(rawTs ?? '').slice(0, 16);
  const MM = pad2(d.getMonth() + 1), DD = pad2(d.getDate());
  const hh = pad2(d.getHours()), mm = pad2(d.getMinutes());
  if (interval === 'Years') return String(d.getFullYear());
  if (interval === 'Months') return `${d.getFullYear()}-${MM}`;
  if (interval === 'Days' || interval === 'Weeks') return `${MM}-${DD}`;
  if (interval === 'Hours') return `${MM}-${DD} ${hh}:00`;
  return `${hh}:${mm}`;
};

const historyStamp = (rawTs) => {
  const d = new Date(rawTs);
  if (isNaN(d.getTime())) return String(rawTs ?? '').slice(0, 32);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ` +
         `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
};

// mean of the first third vs the last third of the series
const seriesTrend = (vals) => {
  if (!vals.length) return 'stable';
  const third = Math.max(1, Math.floor(vals.length / 3));
  const mean = (a) => a.reduce((s, v) => s + v, 0) / a.length;
  const first = mean(vals.slice(0, third));
  const last = mean(vals.slice(-third));
  if (first === 0) return last === 0 ? 'stable' : 'rising';
  if (last > first * 1.05) return 'rising';
  if (last < first * 0.95) return 'falling';
  return 'stable';
};

/**
 * Builds the iot_history block. `rows` must be ordered oldest -> newest.
 * `availableSensors` is the live installed-sensor manifest; when it is empty
 * (history loaded before / without live telemetry) an all-zero column is treated
 * as an uninstalled probe rather than a reading.
 */
const buildIotHistory = (rows, device, interval, availableSensors) => {
  const series = Array.isArray(rows) ? rows : [];
  if (series.length === 0) return null;

  const truncated = series.length > HISTORY_POINT_CAP;
  const kept = truncated ? series.slice(-HISTORY_POINT_CAP) : series;
  const hasManifest = Array.isArray(availableSensors) && availableSensors.length > 0;

  const metrics = [];
  HISTORY_METRICS.forEach((m) => {
    if (metrics.length >= 8) return;
    const vals = kept
      .map((r) => Number(r[m.key]))
      .filter((v) => Number.isFinite(v));
    if (vals.length === 0) return;
    if (hasManifest) {
      if (!availableSensors.includes(m.sensor)) return;
    } else if (vals.every((v) => v === 0)) {
      return;
    }
    const sum = vals.reduce((s, v) => s + v, 0);
    metrics.push({
      key: m.key,
      unit: m.unit,
      min: Number(Math.min(...vals).toFixed(2)),
      max: Number(Math.max(...vals).toFixed(2)),
      avg: Number((sum / vals.length).toFixed(2)),
      latest: Number(vals[vals.length - 1].toFixed(2)),
      trend: seriesTrend(vals)
    });
  });

  const metricKeys = metrics.map((m) => m.key);
  const points = kept.map((r) => {
    const p = { t: historyPointLabel(r.timestamp, interval) };
    metricKeys.forEach((k) => {
      const n = Number(r[k]);
      if (r[k] !== null && r[k] !== undefined && Number.isFinite(n)) p[k] = n;
    });
    return p;
  });

  return {
    device_id: device?.id ?? null,
    device_name: device?.device_name || '',
    interval,
    from: historyStamp(kept[0]?.timestamp),
    to: historyStamp(kept[kept.length - 1]?.timestamp),
    point_count: points.length,
    truncated,
    points,
    metrics
  };
};

// ── Device satellite imagery ───────────────────────────────────────────────
const ESRI_EXPORT_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/export';
const DEVICE_MAP_ZOOM = 17;
const DEVICE_MAP_PX = 1024;
const DEVICE_IMAGERY_CAP = 4;

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(blob);
});

/** Static Esri World Imagery frame centred on lat/lon, returned as a data URI. */
const fetchDeviceImagery = async (lat, lon, timeoutMs = 15000) => {
  const rad = (lat * Math.PI) / 180;
  const metresPerPx = (156543.03392 * Math.cos(rad)) / Math.pow(2, DEVICE_MAP_ZOOM);
  const halfSpanM = (metresPerPx * DEVICE_MAP_PX) / 2;
  const dLat = halfSpanM / 111320;
  const dLon = halfSpanM / (111320 * Math.max(0.05, Math.cos(rad)));
  const bbox = `${lon - dLon},${lat - dLat},${lon + dLon},${lat + dLat}`;
  const url = `${ESRI_EXPORT_URL}?bbox=${bbox}&bboxSR=4326&imageSR=3857` +
              `&size=${DEVICE_MAP_PX},${DEVICE_MAP_PX}&format=jpg&transparent=false&f=image`;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, { signal: controller.signal });
    if (!res.ok) throw new Error(`Imagery HTTP ${res.status}`);
    const blob = await res.blob();
    if (!blob || !String(blob.type).startsWith('image/')) throw new Error('Imagery response was not an image');
    const raw = await blobToDataUrl(blob);
    const capped = await capDataUrl(raw, { maxEdge: MAX_MAP_IMAGE_EDGE_PX, maxBytes: MAX_MAP_IMAGE_BYTES });
    if (capped) return capped;
    return dataUrlBytes(raw) <= MAX_MAP_IMAGE_BYTES ? raw : null;
  } finally {
    clearTimeout(timer);
  }
};

const numOrNull = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};


const IoTView = ({ t, isRTL, lang, setReportData, userId, theme, selectedDevice, setSelectedDevice, setActiveTab, setIsSupportChatOpen, setChatQuickReplies }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dashboardUrl, setDashboardUrl] = useState(null);

  const [devices, setDevices] = useState([]);
  const [deviceHistory, setDeviceHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [weatherData, setWeatherData] = useState(null);
  const [loadingWeather, setLoadingWeather] = useState(false);
  const [historyInterval, setHistoryInterval] = useState("Live");

  const [dataMode, setDataMode] = useState('live');
  const [manualInputs, setManualInputs] = useState({
    Soil_Moisture: 50, Ambient_Temperature: 25, Soil_Temperature: 22,
    Humidity: 60, Soil_pH: 6.5, Nitrogen_Level: 100,
    Phosphorus_Level: 50, Potassium_Level: 150, Electrochemical_Signal: 600,
    Salinity: 800, TDS: 700, Epsilon: 30
  });
  // الخانة ممكن تبقى '' وقت ما المستخدم يمسحها عشان يكتب رقم جديد،
  // فبنحولها لرقم وقت إرسالها للـ API بس
  const toNum = (v) => (v === '' || v === null || v === undefined || isNaN(Number(v)) ? 0 : Number(v));
  const numericManualInputs = () =>
    Object.fromEntries(Object.entries(manualInputs).map(([k, v]) => [k, toNum(v)]));

  const [manualLoading, setManualLoading] = useState(false);
  const [manualResult, setManualResult] = useState(null);
  const [hasCheckedDevices, setHasCheckedDevices] = useState(false);

  // ── RAG Diagnostics State ──
  const [globalContext, setGlobalContext] = useState({});
  const [ragContent, setRagContent] = useState('');
  const [ragStreaming, setRagStreaming] = useState(false);
  const [ragComplete, setRagComplete] = useState(false);
  const [ragError, setRagError] = useState(false);
  const [ragErrorMsg, setRagErrorMsg] = useState('');
  const [ragThinking, setRagThinking] = useState('');
  const [ragIsThinking, setRagIsThinking] = useState(false);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const ragAbortRef = useRef(null);

  // ── Report payload refs (never read a useState value inside an async handler) ──
  // Installed-sensor manifest, TAGGED with the device it describes. The history
  // fetch and fetchData() race: fetchData awaits two requests before it can fill
  // this in, so on a device switch the history for device B could be filtered by
  // device A's manifest - which is how an ambient probe device B does not have
  // ends up in B's history table reading 0.0.
  const sensorsAvailableRef = useRef({ deviceId: null, sensors: [] });
  const deviceImageryRef = useRef([]);      // accumulated device_imagery entries (cap 4)

  // Phase 3: Instant client-side translation fallback
  const previousLangRef = useRef(lang);
  useEffect(() => {
    if (lang && previousLangRef.current !== lang) {
      previousLangRef.current = lang;
      
      // If we have rendered AI content and the user toggled the language
      if (ragContent && ragComplete && !ragStreaming) {
         setRagStreaming(true); // show streaming animation during quick translation
         TranslationService.translateText(ragContent, lang).then(translated => {
             setRagContent(translated);
             setRagStreaming(false);
         }).catch(() => {
             setRagStreaming(false);
         });
      }
    }
  }, [lang, ragContent, ragComplete, ragStreaming]);

  // Mappings
  const SENSOR_MAP = {
    'Soil_Moisture': 'soilM', 'Ambient_Temperature': 'ambT', 'Soil_Temperature': 'soilT',
    'Humidity': 'hum', 'Light_Intensity': 'light', 'Soil_pH': 'ph',
    'Nitrogen_Level': 'nitrogen', 'Phosphorus_Level': 'phosphorus', 'Potassium_Level': 'potassium',
    'Electrochemical_Signal': 'ec', 'Salinity': 'salinity', 'TDS': 'tds', 'Epsilon': 'epsilon'
  };
  const CHART_MAP = {
    'Soil Moisture': 'soilM', 'Ambient Temp': 'ambT', 'Soil Temp': 'soilT', 'Humidity': 'hum',
    'Light': 'light', 'pH': 'ph', 'N': 'nitrogen', 'P': 'phosphorus', 'K': 'potassium', 'EC': 'ec',
    'Salinity': 'salinity', 'TDS': 'tds', 'Epsilon': 'epsilon'
  };

  const getSensorThemeColor = (key) => {
    const map = {
      'Soil_Moisture': '#3b82f6',
      'Ambient_Temperature': '#f97316',
      'Soil_Temperature': '#f97316',
      'Humidity': '#06b6d4',
      'Soil_pH': '#a855f7',
      'Nitrogen_Level': '#84cc16',
      'Phosphorus_Level': '#22c55e',
      'Potassium_Level': '#10b981',
      'Electrochemical_Signal': '#6366f1',
      'Salinity': '#0ea5e9',
      'TDS': '#3b82f6',
      'Epsilon': '#8b5cf6'
    };
    return map[key] || '#C47A2C';
  };

  const fetchData = async (deviceId) => {
    setLoading(true);
    const targetDeviceId = deviceId ?? selectedDevice?.id ?? 1;
    try {
      const json = await ApiService.get(`/iot-status?user_id=${userId}&device_id=${targetDeviceId}`);
      window.dispatchEvent(new Event('refresh-credits'));

      setData(json);
      if (json.dashboard_url) setDashboardUrl(json.dashboard_url);

      const devicesRes = await ApiService.get(`/devices?user_id=${userId}`);
      if (Array.isArray(devicesRes)) setDevices(devicesRes);

      // Presence rule: uninstalled sensors are omitted entirely, never reported as 0.
      const cleanIotData = buildIotData(json.sensors);
      const sensorsAvailable = Object.keys(cleanIotData);
      sensorsAvailableRef.current = { deviceId: targetDeviceId, sensors: sensorsAvailable };

      const status = json.ai_diagnosis?.status || "Unknown";
      const conf = readConfidence(json.ai_diagnosis);

      const dev = (selectedDevice && selectedDevice.id === targetDeviceId) ? selectedDevice : null;
      const iotDevice = {
        device_id: targetDeviceId,
        device_name: dev?.device_name || '',
        installed_location: dev?.installed_location || '',
        latitude: numOrNull(dev?.latitude),
        longitude: numOrNull(dev?.longitude),
        mqtt_status: json.mqtt_status || 'Unknown',
        data_source: 'live'
      };

      commitModule(setReportData, 'iot', {
        iot_data: cleanIotData,
        iot_sensors_available: sensorsAvailable,
        iot_device: iotDevice,
        iot_ai_status: status,
        iot_ai_confidence: conf
      }, sensorsAvailable.length > 0,
        sensorsAvailable.length > 0 ? null : 'No live sensor readings available for this device.');

      setTimeout(() => {
        setLoading(false);
      }, 800);

    } catch (e) {
      console.error("IoT Fetch Error:", e);
      /* Clear the payload, do not just mark the run failed. The previously
         selected device's readings are still sitting in reportData, and a bare
         markRun leaves them there while reporting ran=true - so the export
         would print device A's telemetry under device B's name. */
      commitModule(setReportData, 'iot', {
        iot_data: {},
        iot_sensors_available: [],
        iot_device: {},
        iot_ai_status: '',
        iot_ai_confidence: 0
      }, false, (e?.message || 'IoT telemetry fetch failed').slice(0, 200));
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      ApiService.get(`/devices?user_id=${userId}`).then(res => {
        if (Array.isArray(res)) setDevices(res);
        setHasCheckedDevices(true);
      }).catch(e => {
        console.error("Device fetch error:", e);
        setHasCheckedDevices(true);
      });
    } else {
      setHasCheckedDevices(true);
    }
  }, [userId]);

  useEffect(() => {
    if (selectedDevice) {
      // 0. Drop the previous device's sensor manifest before anything can read
      //    it. fetchData() refills it, tagged with the new device id.
      sensorsAvailableRef.current = { deviceId: null, sensors: [] };

      // 1. Fetch fresh live telemetry for the newly selected device
      fetchData(selectedDevice.id);
      
      // 2. Reset diagnostic panel payload state
      setShowDiagnostics(false);
      setRagContent('');
      setRagComplete(false);
      setRagError(false);
      setRagErrorMsg('');

      setLoadingHistory(true);
      setLoadingWeather(true);
      setWeatherData(null);

      let useFallback = false;
      try {
        const topics = typeof selectedDevice.mqtt_topics === 'string'
          ? JSON.parse(selectedDevice.mqtt_topics)
          : (selectedDevice.mqtt_topics || []);
        useFallback = topics.includes("WEATHER_API_FALLBACK");
      } catch (e) { }

      const limit = historyInterval === 'Hours' ? 48 : 50;
      const historyPromise = ApiService.get(`/iot-history/${selectedDevice.id}?limit=${limit}&interval=${historyInterval}`);

      const lat = selectedDevice.latitude || 30.095;
      const lon = selectedDevice.longitude || 31.315;

      const meteoUrl = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,relative_humidity_2m,precipitation,rain,cloud_cover,wind_speed_10m,shortwave_radiation`;
      const meteoPromise = fetch(meteoUrl).then(res => res.json()).catch(() => null);

      const nominatimUrl = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`;
      const nominatimPromise = fetch(nominatimUrl, { headers: { 'User-Agent': 'GeoNutria/1.0' } }).then(res => res.json()).catch(() => null);

      Promise.all([historyPromise, meteoPromise, nominatimPromise]).then(([historyRes, meteoData, nomData]) => {
        let currentWeatherData = null;
        if (meteoData && meteoData.current) {
          currentWeatherData = {
            temperature: meteoData.current.temperature_2m,
            humidity: meteoData.current.relative_humidity_2m,
            precipitation: meteoData.current.precipitation,
            rain: meteoData.current.rain,
            clouds: meteoData.current.cloud_cover,
            wind: meteoData.current.wind_speed_10m,
            solar: meteoData.current.shortwave_radiation,
            locationName: nomData && nomData.display_name ? nomData.display_name.split(',')[0] : selectedDevice.installed_location
          };
          setWeatherData(currentWeatherData);
        }
        setLoadingWeather(false);

        // ── Report payload: live weather block ──
        try {
          if (currentWeatherData) {
            commitModule(setReportData, 'weather', {
              weather_data: {
                location_name: currentWeatherData.locationName || '',
                latitude: numOrNull(lat),
                longitude: numOrNull(lon),
                temperature_c: numOrNull(currentWeatherData.temperature),
                relative_humidity_pct: numOrNull(currentWeatherData.humidity),
                precipitation_mm: numOrNull(currentWeatherData.precipitation),
                rain_mm: numOrNull(currentWeatherData.rain),
                cloud_cover_pct: numOrNull(currentWeatherData.clouds),
                wind_speed_kmh: numOrNull(currentWeatherData.wind),
                solar_radiation_wm2: numOrNull(currentWeatherData.solar),
                captured_at: new Date().toISOString(),
                source: 'open-meteo'
              }
            }, true, null);
          } else {
            commitModule(setReportData, 'weather', { weather_data: {} }, false,
              'Live weather could not be retrieved for this location.');
          }
        } catch (err) {
          console.error('Weather report payload error:', err);
        }

        if (historyRes && historyRes.status === 'success' && historyRes.data) {
          const reversed = [...historyRes.data].reverse();
          const formatted = reversed.map(d => {
            const date = new Date(d.timestamp);
            let timeLabel = '';
            const locale = isRTL ? 'ar-EG' : 'en-US';

            if (historyInterval === 'Live') {
              timeLabel = date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
            } else if (historyInterval === 'Hours') {
              timeLabel = date.toLocaleString(locale, { weekday: 'short', hour: '2-digit', minute: '2-digit' });
            } else if (historyInterval === 'Days' || historyInterval === 'Weeks') {
              timeLabel = date.toLocaleDateString(locale, { weekday: 'long', month: 'short', day: 'numeric' });
            } else if (historyInterval === 'Months') {
              timeLabel = date.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
            } else if (historyInterval === 'Years') {
              timeLabel = date.toLocaleDateString(locale, { year: 'numeric' });
            } else {
              timeLabel = date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
            }

            // تطبيق القسمة على بيانات الهيستوري ديناميكياً
            const processedData = { ...d, timeLabel };
            ALL_SENSORS.forEach(sensor => {
              if (sensor.divideBy && processedData[sensor.historyKey] !== undefined && processedData[sensor.historyKey] !== null) {
                processedData[sensor.historyKey] = Number((processedData[sensor.historyKey] / sensor.divideBy).toFixed(2));
              }
            });

            return processedData;
          });

          if (useFallback && currentWeatherData && formatted.length > 0) {
            const latest = formatted[formatted.length - 1];
            if (Number(latest.temperature) === 0 && Number(latest.humidity) === 0) {
              latest.temperature = currentWeatherData.temperature;
              latest.humidity = currentWeatherData.humidity;
              latest.is_weather_fallback = true;
            }
          }

          setDeviceHistory(formatted);

          // ── Report payload: historical readings (oldest -> newest, capped at 24) ──
          try {
            /* Use the manifest ONLY if it describes the device we are building
               history for. A manifest from the previously selected device would
               admit metrics this device has no probe for, printing them as 0.0.
               Passing [] instead falls back to the all-zeros rule, which is the
               safe answer when the manifest is not known yet. */
            const manifest = sensorsAvailableRef.current;
            const manifestSensors =
              manifest && manifest.deviceId === selectedDevice?.id ? manifest.sensors : [];
            const historyBlock = buildIotHistory(
              formatted, selectedDevice, historyInterval, manifestSensors
            );
            if (historyBlock && historyBlock.points.length > 0) {
              commitModule(setReportData, 'history', { iot_history: historyBlock }, true, null);
            } else {
              commitModule(setReportData, 'history', { iot_history: {} }, false,
                'No historical readings available for this device.');
            }
          } catch (err) {
            console.error('History report payload error:', err);
            commitModule(setReportData, 'history', { iot_history: {} }, false,
              (err?.message || 'History payload failed').slice(0, 200));
          }
        } else {
          commitModule(setReportData, 'history', { iot_history: {} }, false,
            'No historical readings available for this device.');
        }
        setLoadingHistory(false);
      }).catch((e) => {
        console.error("Error fetching device data:", e);
        // Same reason as the iot catch: drop the previous device's history
        // rather than leaving them attributed to this one.
        commitModule(setReportData, 'history', { iot_history: {} }, false,
          (e?.message || 'History fetch failed').slice(0, 200));
        setLoadingHistory(false);
        setLoadingWeather(false);
      });
    }
  }, [selectedDevice, historyInterval]);

  // ── Report payload: device satellite imagery (Esri World Imagery, base64) ──
  // Devices without coordinates are excluded entirely. Purely additive to the
  // report — never blocks or alters the on-screen map.
  useEffect(() => {
    if (!selectedDevice) return undefined;
    const lat = numOrNull(selectedDevice.latitude);
    const lon = numOrNull(selectedDevice.longitude);
    if (lat === null || lon === null) return undefined;

    let cancelled = false;

    (async () => {
      let image = null;
      let errMsg = null;
      try {
        image = await fetchDeviceImagery(lat, lon);
        if (!image) errMsg = 'Device imagery exceeded the report size budget.';
      } catch (e) {
        errMsg = String(e?.message || 'Device imagery unavailable').slice(0, 200);
      }
      if (cancelled) return;

      const entry = {
        device_id: selectedDevice.id,
        device_name: selectedDevice.device_name || '',
        installed_location: selectedDevice.installed_location || '',
        latitude: lat,
        longitude: lon,
        image_base64: image,
        image_source: 'esri-world-imagery',
        zoom: DEVICE_MAP_ZOOM,
        captured_at: new Date().toISOString(),
        attribution: 'Imagery © Esri, Maxar, Earthstar Geographics'
      };

      const previous = Array.isArray(deviceImageryRef.current) ? deviceImageryRef.current : [];
      const merged = [entry, ...previous.filter(d => d.device_id !== entry.device_id)]
        .slice(0, DEVICE_IMAGERY_CAP);
      deviceImageryRef.current = merged;

      commitModule(setReportData, 'device_map', { device_imagery: merged },
        Boolean(image), errMsg);
    })();

    return () => { cancelled = true; };
  }, [selectedDevice, setReportData]);

  const handleManualSubmit = async () => {
    setManualLoading(true);
    try {
      const m = numericManualInputs();
      const payload = {
        user_id: userId || 1,
        moisture: m.Soil_Moisture,
        ambient_temp: m.Ambient_Temperature,
        soil_temp: m.Soil_Temperature,
        humidity: m.Humidity,
        ph: m.Soil_pH,
        nitrogen: m.Nitrogen_Level,
        phosphorus: m.Phosphorus_Level,
        potassium: m.Potassium_Level,
        ec: m.Electrochemical_Signal
      };

      const res = await ApiService.post('/manual-diagnosis', payload);
      window.dispatchEvent(new Event('refresh-credits'));

      setManualResult(res.ai_diagnosis);

      // Same presence rule as the live path: a manually left-at-zero probe that
      // cannot physically read 0 is treated as absent rather than reported as "0".
      const manualIotData = buildIotData(m);
      const manualSensors = Object.keys(manualIotData);
      sensorsAvailableRef.current = { deviceId: selectedDevice?.id ?? null, sensors: manualSensors };

      commitModule(setReportData, 'iot', {
        iot_data: manualIotData,
        iot_sensors_available: manualSensors,
        iot_device: {
          device_id: selectedDevice?.id ?? null,
          device_name: selectedDevice?.device_name || '',
          installed_location: selectedDevice?.installed_location || '',
          latitude: numOrNull(selectedDevice?.latitude),
          longitude: numOrNull(selectedDevice?.longitude),
          mqtt_status: 'Manual Entry',
          data_source: 'manual'
        },
        iot_ai_status: res.ai_diagnosis?.status || "Unknown",
        iot_ai_confidence: readConfidence(res.ai_diagnosis)
      }, true, null);
    } catch (e) {
      markRun(setReportData, 'iot', false, (e?.message || 'Manual diagnosis failed').slice(0, 200));
      alert("Failed to submit manual diagnosis");
    }
    setManualLoading(false);
  };

  // ── RAG Deep Diagnosis (replaces RF model) ──
  const handleContextChange = useCallback((ctx) => {
    setGlobalContext(ctx);
  }, []);

  const runRAGDiagnosis = async () => {
    // Abort any previous stream
    if (ragAbortRef.current) ragAbortRef.current.abort();

    // Collect sensor data
    let sensorPayload = {};
    if (dataMode === 'manual') {
      sensorPayload = numericManualInputs();
    } else if (data?.sensors) {
      sensorPayload = { ...data.sensors };
    }

    // Collect weather data
    const weatherPayload = weatherData ? { ...weatherData } : {};

    // Reset and show panel
    setRagContent('');
    setRagThinking('');
    setRagIsThinking(false);
    setRagStreaming(true);
    setRagComplete(false);
    setRagError(false);
    setRagErrorMsg('');
    setShowDiagnostics(true);

    ragAbortRef.current = new AbortController();

    try {
      const response = await fetch(`${ApiService.BASE_URL}/v1/rag-diagnosis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: ragAbortRef.current.signal,
        body: JSON.stringify({
          sensor_data: sensorPayload,
          weather_data: weatherPayload,
          context: globalContext,
          // الموديل لازم يعرف إن القراءات دي متكتبة يدوي مش جاية من سنسور
          data_source: dataMode === 'manual' ? 'manual' : 'live',
          lang: lang || (isRTL ? 'ar' : 'en')
        })
      });

      if (!response.ok) throw new Error(`API Error (${response.status})`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let sseBuffer = ''; // Holds incomplete SSE data between network chunks
      let inThinking = false;
      let fullContent = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        if (!value) continue;

        // Prepend any leftover data from the previous chunk
        sseBuffer += decoder.decode(value, { stream: true });

        // Split on double-newline (SSE message boundary)
        const parts = sseBuffer.split('\n\n');
        // The last element may be incomplete — keep it in the buffer
        sseBuffer = parts.pop() || '';

        for (const part of parts) {
          const lines = part.split('\n').filter(l => l.trim());
          for (let line of lines) {
            if (line.startsWith('data: ')) line = line.slice(6);
            if (line === '[DONE]') continue;

            let parsed;
            try {
              parsed = JSON.parse(line);
            } catch {
              // Truly malformed line — skip
              continue;
            }

            if (parsed.error) {
              setRagError(true);
              setRagErrorMsg(parsed.error.message || parsed.error || 'Unknown error');
              continue;
            }

            let tokenContent = '';
            let tokenReasoning = '';
            const delta = parsed.choices?.[0]?.delta;
            if (delta) {
              if (delta.content !== undefined) tokenContent = delta.content;
              if (delta.reasoning !== undefined) tokenReasoning = delta.reasoning;
            } else if (parsed.message?.content !== undefined) {
              tokenContent = parsed.message.content;
            }

            if (!tokenContent && !tokenReasoning) continue;

            // Route reasoning tokens to thinking state
            if (tokenReasoning) {
              setRagIsThinking(true);
              setRagThinking(prev => prev + tokenReasoning);
              continue;
            }

            // Handle <think>...</think> tags in content stream
            if (tokenContent.includes('<think>')) {
              inThinking = true;
              setRagIsThinking(true);
              const parts = tokenContent.split('<think>');
              if (parts[0]) setRagContent(prev => prev + parts[0]);
              if (parts[1]) setRagThinking(prev => prev + parts[1]);
              continue;
            }
            if (inThinking && tokenContent.includes('</think>')) {
              inThinking = false;
              setRagIsThinking(false);
              const parts = tokenContent.split('</think>');
              if (parts[0]) setRagThinking(prev => prev + parts[0]);
              if (parts[1]) setRagContent(prev => prev + parts[1]);
              continue;
            }
            if (inThinking) {
              setRagThinking(prev => prev + tokenContent);
              continue;
            }

            // Regular content token — thinking is done
            if (ragIsThinking) setRagIsThinking(false);

            if (tokenContent) {
              setRagContent(prev => prev + tokenContent);
              fullContent += tokenContent;
            }
          }
        }
      }

      setRagComplete(true);
      // Deep RAG analysis has its own dedicated field — never append to
      // ai_consultant_text (owned solely by AIConsultantView). fullContent is a
      // local accumulator, not the ragContent state (which is a stale closure here).
      commitModule(setReportData, 'iot', { iot_rag_text: fullContent }, true, null);
    } catch (err) {
      if (err.name !== 'AbortError') {
        setRagError(true);
        setRagErrorMsg(err.message);
      }
    } finally {
      setRagStreaming(false);
      ragAbortRef.current = null;
    }
  };


  // ==========================================
  // DYNAMIC SENSORS FILTERING LOGIC
  // ==========================================
  // ==========================================
  // DYNAMIC SENSORS DEFINITIONS WITH ALGORITHMIC RANGES
  // ==========================================
  // تعريف الـ 12 سنسور مع الأيقونات المناسبة، ونطاقات الألوان
  // منطق الألوان: 
  // - linearLowToHigh: الأحمر (منخفض جداً)، الأصفر (متوسط)، الأخضر (جيد) [مثلاً الرطوبة]
  // - linearHighToLow: الأخضر (منخفض)، الأصفر (متوسط)، الأحمر (مرتفع جداً/مجهد) [مثلاً الملوحة]
  // - bellCurve: الأخضر (في المنتصف)، الأصفر والأحمر على الأطراف [مثلاً الـ pH]

  const ALL_SENSORS = [
    {
      key: 'Soil_Moisture', historyKey: 'moisture', label: t.dashboard.soilM, unit: t?.common?.units?.percent || '%',
      icon: Droplets, // أيقونة قطرات ماء
      iconAnimation: 'animate-icon-pulse',
      colorConfig: {
        logic: 'bellCurve',
        ranges: { redLow: 20, yellowLow: 40, green: 70, yellowHigh: 85, redHigh: 100 },
        colors: { red: 'text-red-500', yellow: 'text-yellow-500', green: 'text-emerald-500' }
      }
    },
    {
      key: 'Ambient_Temperature', historyKey: 'temperature', label: t.dashboard.ambT, unit: t?.common?.units?.celsius || '\u00b0C',
      icon: ThermometerSun, // ترمومتر وشمس
      iconAnimation: 'animate-icon-float',
      colorConfig: {
        logic: 'bellCurve',
        ranges: { redLow: 10, yellowLow: 18, green: 30, yellowHigh: 38, redHigh: 50 },
        colors: { red: 'text-red-500', yellow: 'text-yellow-500', green: 'text-emerald-500' }
      }
    },
    {
      key: 'Soil_Temperature', historyKey: 'soil_temp', label: t.dashboard.soilT, unit: t?.common?.units?.celsius || '\u00b0C',
      icon: ThermometerSun, // ترمومتر وشمس (للتربة)
      iconAnimation: 'animate-icon-float',
      colorConfig: {
        logic: 'bellCurve',
        ranges: { redLow: 10, yellowLow: 18, green: 30, yellowHigh: 38, redHigh: 50 },
        colors: { red: 'text-red-500', yellow: 'text-yellow-500', green: 'text-emerald-500' }
      }
    },
    {
      key: 'Humidity', historyKey: 'humidity', label: t.dashboard.hum, unit: t?.common?.units?.percent || '%',
      icon: Wind, // أيقونة رياح/رطوبة جوية
      iconAnimation: 'animate-icon-sway',
      colorConfig: {
        logic: 'bellCurve',
        ranges: { redLow: 20, yellowLow: 40, green: 70, yellowHigh: 85, redHigh: 100 },
        colors: { red: 'text-red-500', yellow: 'text-yellow-500', green: 'text-emerald-500' }
      }
    },
    {
      key: 'Soil_pH', historyKey: 'ph', label: t.dashboard.ph, unit: '',
      icon: FlaskConical, // دورق مختبر (للحموضة)
      iconAnimation: 'animate-icon-sway',
      colorConfig: {
        logic: 'bellCurve',
        ranges: { redLow: 4.5, yellowLow: 5.5, green: 7.5, yellowHigh: 8.5, redHigh: 14 },
        colors: { red: 'text-red-500', yellow: 'text-yellow-500', green: 'text-emerald-500' }
      }
    },
    {
      key: 'Nitrogen_Level', historyKey: 'nitrogen', label: t.dashboard.nitrogen, unit: t?.common?.units?.mg_kg || 'mg/kg',
      icon: Leaf, // ورقة نبات (عنصر مغذي)
      iconAnimation: 'animate-icon-sway',
      colorConfig: {
        logic: 'linearLowToHigh',
        ranges: { red: 50, yellow: 120, green: 300 }, // نقص أحمر، كافي أخضر
        colors: { red: 'text-red-500', yellow: 'text-yellow-500', green: 'text-emerald-500' }
      }
    },
    {
      key: 'Phosphorus_Level', historyKey: 'phosphorus', label: t.dashboard.phosphorus, unit: t?.common?.units?.mg_kg || 'mg/kg',
      icon: Leaf, // ورقة نبات
      iconAnimation: 'animate-icon-sway',
      colorConfig: {
        logic: 'linearLowToHigh',
        ranges: { red: 30, yellow: 80, green: 200 },
        colors: { red: 'text-red-500', yellow: 'text-yellow-500', green: 'text-emerald-500' }
      }
    },
    {
      key: 'Potassium_Level', historyKey: 'potassium', label: t.dashboard.potassium, unit: t?.common?.units?.mg_kg || 'mg/kg',
      icon: Leaf, // ورقة نبات
      iconAnimation: 'animate-icon-sway',
      colorConfig: {
        logic: 'linearLowToHigh',
        ranges: { red: 80, yellow: 180, green: 400 },
        colors: { red: 'text-red-500', yellow: 'text-yellow-500', green: 'text-emerald-500' }
      }
    },
    {
      key: 'Electrochemical_Signal', historyKey: 'ec', label: t.dashboard.ec, unit: t?.common?.units?.mS_cm || 'mS/cm',
      icon: Waves,
      iconAnimation: 'animate-icon-wave',
      divideBy: 1000,
      colorConfig: {
        logic: 'linearHighToLow',
        // الأخضر لحد 0.5
        // الأصفر يبدأ من 0.5 لحد 2.0
        // الأحمر يبدأ من بعد 2.0 (وحطينا 3.0 كحد أقصى لرسم الشارت)
        ranges: { green: 0.5, yellow: 2.0, red: 3.0 },
        colors: { red: 'text-red-500', yellow: 'text-yellow-500', green: 'text-emerald-500' }
      },
    },
    {
      key: 'Salinity', historyKey: 'salinity', label: t.dashboard.salinity || 'Salinity', unit: t?.common?.units?.mg_L || 'mg/L',
      icon: Waves, // أمواج
      iconAnimation: 'animate-icon-wave',
      colorConfig: {
        logic: 'linearHighToLow', // الملوحة العالية سيئة
        ranges: { green: 600, yellow: 1200, red: 3000 },
        colors: { red: 'text-red-500', yellow: 'text-yellow-500', green: 'text-emerald-500' }
      }
    },
    {
      key: 'TDS', historyKey: 'tds', label: t.dashboard.tds || 'TDS', unit: t?.common?.units?.mg_L || 'mg/L',
      icon: Droplets, // قطرات (مواد ذائبة في الماء)
      iconAnimation: 'animate-icon-pulse',
      colorConfig: {
        logic: 'linearHighToLow', // الـ TDS العالي سيئ
        ranges: { green: 500, yellow: 1000, red: 2500 },
        colors: { red: 'text-red-500', yellow: 'text-yellow-500', green: 'text-emerald-500' }
      }
    },
    {
      key: 'Epsilon', historyKey: 'epsilon', label: t.dashboard.epsilon || 'Epsilon', unit: '',
      icon: Activity, // نشاط (تعبر عن السماحية الكهربية للتربة)
      iconAnimation: 'animate-icon-pulse',
      colorConfig: {
        logic: 'linearLowToHigh', // مرتبطة بالرطوبة، العالي جيد
        ranges: { red: 15, yellow: 35, green: 80 },
        colors: { red: 'text-red-500', yellow: 'text-yellow-500', green: 'text-emerald-500' }
      }
    }
  ];

  const activeSensors = ALL_SENSORS.map(sensor => {
    let displayVal = 0;
    let isFromHistory = false; // متغير عشان نعرف الداتا جاية منين

    if (dataMode === 'manual') {
      displayVal = manualInputs[sensor.key];
    } else if (dataMode === 'live' && data?.sensors) {
      displayVal = data.sensors[sensor.key] ?? 0;
    } else {
      // لو الداتا جاية من الهيستوري
      displayVal = deviceHistory.length > 0 ? deviceHistory[deviceHistory.length - 1][sensor.historyKey] : 0;
      isFromHistory = true;
    }

    // تطبيق القسمة وتقريب الرقم فقط لو الداتا "مش" جاية من الهيستوري
    if (!isFromHistory && sensor.divideBy && displayVal !== undefined && displayVal !== null && displayVal !== 0) {
      displayVal = Number((displayVal / sensor.divideBy).toFixed(2));
    } else if (isFromHistory && displayVal !== undefined && displayVal !== null) {
      // لو من الهيستوري هنقرب الرقم بس من غير ما نقسمه تاني
      displayVal = Number(Number(displayVal).toFixed(2));
    }

    return { ...sensor, displayVal };
  }).filter(sensor => {
    if (dataMode === 'manual') return true;
    return sensor.displayVal !== 0 && sensor.displayVal !== "0" && sensor.displayVal !== undefined && sensor.displayVal !== null;
  });
  const healthStatus = data?.ai_diagnosis?.status || "Unknown";
  const isHealthy = healthStatus === "Healthy";
  let confidence = data?.ai_diagnosis?.probabilities?.[healthStatus] || 0;
  const confidencePercent = (confidence * 100).toFixed(1);

  const manualHealthStatus = manualResult?.status || "Unknown";
  const manualIsHealthy = manualHealthStatus === "Healthy";
  const manualConfidence = manualResult?.probabilities?.[manualHealthStatus] || 0;
  const manualConfidencePercent = (manualConfidence * 100).toFixed(1);


  if (loading && !data) return <div className="h-full flex items-center justify-center flex-col gap-4">
    <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-emerald-500"></div>
    <p className="text-emerald-400 animate-pulse">{t.iot.connecting}</p>
  </div>;

  // دالة لحساب ألوان الرسم البياني بناءً على النطاقات والقيم
  // دالة لحساب ألوان الرسم البياني بناءً على النطاقات والقيم
  const getGradientStops = (sensor, data, opacity = 1) => {
    if (!sensor.colorConfig || !data || data.length === 0) return null;

    // استخراج القيم لحساب الحد الأقصى والأدنى في الشارت للخط نفسه
    const vals = data.map(d => d[sensor.historyKey]).filter(v => v !== undefined && v !== null);
    if (vals.length === 0) return null;

    const dataMin = Math.min(...vals);
    const dataMax = Math.max(...vals);

    // ربط أسماء الـ Tailwind Classes بأكواد الألوان Hex للـ SVG
    const hexMap = {
      'text-red-500': '#ef4444',
      'text-yellow-500': '#eab308',
      'text-emerald-500': '#10b981'
    };

    const { logic, ranges, colors } = sensor.colorConfig;

    // 1. حالة الخط المستقيم تماماً (القراءات كلها متساوية)
    if (dataMax === dataMin) {
      const val = dataMax;
      let color = hexMap[colors.green];
      if (logic === 'linearLowToHigh') {
        if (val <= ranges.red) color = hexMap[colors.red];
        else if (val <= ranges.yellow) color = hexMap[colors.yellow];
        else color = hexMap[colors.green];
      } else if (logic === 'linearHighToLow') {
        if (val <= ranges.green) color = hexMap[colors.green];
        else if (val <= ranges.yellow) color = hexMap[colors.yellow];
        else color = hexMap[colors.red];
      } else if (logic === 'bellCurve') {
        if (val <= ranges.redLow) color = hexMap[colors.red];
        else if (val <= ranges.yellowLow) color = hexMap[colors.yellow];
        else if (val <= ranges.green) color = hexMap[colors.green];
        else if (val <= ranges.yellowHigh) color = hexMap[colors.yellow];
        else color = hexMap[colors.red];
      }
      return <stop offset="0%" stopColor={color} stopOpacity={opacity} />;
    }

    // 2. الحسبة الدقيقة بدون تحجيم (Clamping) عشان الألوان متتحشرش في مساحة ضيقة
    const getOffset = (val) => {
      // y1="0" (0%) هو أعلى نقطة في الخط (dataMax)
      // y2="1" (100%) هو أقل نقطة في الخط (dataMin)
      const p = (dataMax - val) / (dataMax - dataMin);
      return `${p * 100}%`;
    };

    if (logic === 'linearLowToHigh') {
      return (
        <>
          <stop offset={getOffset(1000)} stopColor={hexMap[colors.green]} stopOpacity={opacity} />
          <stop offset={getOffset(ranges.yellow)} stopColor={hexMap[colors.green]} stopOpacity={opacity} />
          <stop offset={getOffset(ranges.yellow)} stopColor={hexMap[colors.yellow]} stopOpacity={opacity} />
          <stop offset={getOffset(ranges.red)} stopColor={hexMap[colors.yellow]} stopOpacity={opacity} />
          <stop offset={getOffset(ranges.red)} stopColor={hexMap[colors.red]} stopOpacity={opacity} />
          <stop offset={getOffset(-1000)} stopColor={hexMap[colors.red]} stopOpacity={opacity} />
        </>
      );
    } else if (logic === 'linearHighToLow') {
      return (
        <>
          <stop offset={getOffset(1000)} stopColor={hexMap[colors.red]} stopOpacity={opacity} />
          <stop offset={getOffset(ranges.yellow)} stopColor={hexMap[colors.red]} stopOpacity={opacity} />
          <stop offset={getOffset(ranges.yellow)} stopColor={hexMap[colors.yellow]} stopOpacity={opacity} />
          <stop offset={getOffset(ranges.green)} stopColor={hexMap[colors.yellow]} stopOpacity={opacity} />
          <stop offset={getOffset(ranges.green)} stopColor={hexMap[colors.green]} stopOpacity={opacity} />
          <stop offset={getOffset(-1000)} stopColor={hexMap[colors.green]} stopOpacity={opacity} />
        </>
      );
    } else if (logic === 'bellCurve') {
      return (
        <>
          <stop offset={getOffset(1000)} stopColor={hexMap[colors.red]} stopOpacity={opacity} />
          <stop offset={getOffset(ranges.yellowHigh)} stopColor={hexMap[colors.red]} stopOpacity={opacity} />
          <stop offset={getOffset(ranges.yellowHigh)} stopColor={hexMap[colors.yellow]} stopOpacity={opacity} />
          <stop offset={getOffset(ranges.green)} stopColor={hexMap[colors.yellow]} stopOpacity={opacity} />
          <stop offset={getOffset(ranges.green)} stopColor={hexMap[colors.green]} stopOpacity={opacity} />
          <stop offset={getOffset(ranges.yellowLow)} stopColor={hexMap[colors.green]} stopOpacity={opacity} />
          <stop offset={getOffset(ranges.yellowLow)} stopColor={hexMap[colors.yellow]} stopOpacity={opacity} />
          <stop offset={getOffset(ranges.redLow)} stopColor={hexMap[colors.yellow]} stopOpacity={opacity} />
          <stop offset={getOffset(ranges.redLow)} stopColor={hexMap[colors.red]} stopOpacity={opacity} />
          <stop offset={getOffset(-1000)} stopColor={hexMap[colors.red]} stopOpacity={opacity} />
        </>
      );
    }
    return <stop offset="0%" stopColor="#C47A2C" stopOpacity={opacity} />;
  };

  // دالة لتثبيت أبعاد محور Y لضمان دقة التدرج اللوني
  // دالة لحساب مقياس الرسم (Domain) والأرقام المرجعية (Ticks) بناءً على تكوين السنسور
  const getChartScale = (sensor, data) => {
    let min = 0;
    let max = 100;
    let ticks = [];

    if (sensor.colorConfig && sensor.colorConfig.ranges) {
      const r = sensor.colorConfig.ranges;
      const logic = sensor.colorConfig.logic;

      if (logic === 'linearLowToHigh') {
        max = r.green || 100;
        ticks = [0, r.red, r.yellow, r.green];
      } else if (logic === 'linearHighToLow') {
        max = r.red || 100;
        ticks = [0, r.green, r.yellow, r.red];
      } else if (logic === 'bellCurve') {
        min = 0;
        max = (r.redHigh || r.yellowHigh || 14) + 1;
        ticks = [r.redLow, r.yellowLow, r.green, r.yellowHigh, r.redHigh].filter(Boolean);
      }
    }

    // التأكد إن الداتا الحالية مش بتعدي المقياس (عشان الشارت ميبقاش مقطوع لو القراءة شاذة)
    if (data && data.length > 0) {
      const vals = data.map(d => d[sensor.historyKey]).filter(v => v !== undefined && v !== null);
      if (vals.length > 0) {
        const dataMax = Math.max(...vals);
        if (dataMax > max) max = Math.ceil(dataMax * 1.1); // نزود 10% مساحة فاضية من فوق
      }
    }

    // إزالة التكرار وترتيب الأرقام
    ticks = [...new Set(ticks)].sort((a, b) => a - b);
    return { domain: [min, max], ticks };
  };

  // دالة لرسم خلفيات الشارت (الزونز)
  const renderReferenceZones = (sensor, domainMax) => {
    if (!sensor.colorConfig || !sensor.colorConfig.ranges) return null;
    const r = sensor.colorConfig.ranges;
    const logic = sensor.colorConfig.logic;
    const opacity = theme === 'dark' ? 0.15 : 0.25;
    if (logic === 'linearLowToHigh') {
      return (
        <>
          <ReferenceArea y1={0} y2={r.red} fill="#ef4444" fillOpacity={opacity} />
          <ReferenceArea y1={r.red} y2={r.yellow} fill="#eab308" fillOpacity={opacity} />
          <ReferenceArea y1={r.yellow} y2={domainMax} fill="#10b981" fillOpacity={opacity} />
        </>
      );
    } else if (logic === 'linearHighToLow') {
      return (
        <>
          <ReferenceArea y1={0} y2={r.green} fill="#10b981" fillOpacity={opacity} />
          <ReferenceArea y1={r.green} y2={r.yellow} fill="#eab308" fillOpacity={opacity} />
          <ReferenceArea y1={r.yellow} y2={domainMax} fill="#ef4444" fillOpacity={opacity} />
        </>
      );
    } else if (logic === 'bellCurve') {
      return (
        <>
          <ReferenceArea y1={0} y2={r.redLow} fill="#ef4444" fillOpacity={opacity} />
          <ReferenceArea y1={r.redLow} y2={r.yellowLow} fill="#eab308" fillOpacity={opacity} />
          <ReferenceArea y1={r.yellowLow} y2={r.green} fill="#10b981" fillOpacity={opacity} />
          <ReferenceArea y1={r.green} y2={r.yellowHigh} fill="#eab308" fillOpacity={opacity} />
          <ReferenceArea y1={r.yellowHigh} y2={domainMax} fill="#ef4444" fillOpacity={opacity} />
        </>
      );
    }
    return null;
  };

  return (
    <div className="pb-10 font-sans text-[#1B263B]">

      {!loading && !data && dataMode === 'manual' && (
        <div className="mb-4 inline-flex items-center gap-2 px-3 py-1 bg-yellow-500/10 text-yellow-700 text-sm font-medium rounded-full">
          <span>{t.iot.telemetryOffline}</span>
        </div>
      )}

      {/* Main Grid container */}
      <div className="grid grid-cols-12 gap-6 mt-2">

        {/* Top Control Row */}
        <div className="col-span-12 min-w-0 flex flex-col md:flex-row justify-between items-center mb-2">
          {/* Segmented Toggle */}
          <div className="inline-flex bg-white dark:bg-slate-800 rounded-xl shadow-sm p-1 border border-[#1B263B]/5 dark:border-slate-700">
            <button onClick={() => setDataMode('live')} className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${dataMode === 'live' ? 'bg-[#6B8F71] text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-[#1B263B] dark:hover:text-white'}`}>
              {t.iot.liveConnect}
            </button>
            <button onClick={() => setDataMode('manual')} className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${dataMode === 'manual' ? 'bg-[#6B8F71] text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-[#1B263B] dark:hover:text-white'}`}>
              {t.iot.manualEntry}
            </button>
          </div>

          {dataMode === 'live' && (
            <select
              className="mt-4 md:mt-0 md:me-4 bg-white dark:bg-slate-800 border border-[#1B263B]/10 dark:border-slate-700 rounded-xl px-4 py-2 text-sm font-medium shadow-sm outline-none focus:ring-2 focus:ring-[#6B8F71]/50 text-[#1B263B] dark:text-white transition-colors flex-1 md:max-w-xs"
              onChange={(e) => setSelectedDevice(devices.find(d => d.id == e.target.value))}
              value={selectedDevice?.id || ""}
            >
              <option value="" disabled>{t.iot.selectDevice}</option>
              {devices.map(d => <option key={d.id} value={d.id}>{d.device_name} </option>)}
            </select>
          )}

          {selectedDevice && dataMode === 'live' && (
            <button
              onClick={async () => {
                try {
                  await ApiService.get(`/iot/refresh/${selectedDevice.id}`);
                  await fetchData(selectedDevice.id);
                } catch (e) { }
              }}
              className="mt-4 md:mt-0 md:me-4 px-4 py-2 bg-[#6B8F71] hover:bg-[#527056] text-white rounded-xl shadow-[inset_0_-2px_4px_rgba(0,0,0,0.1)] transition-transform transform hover:-translate-y-0.5 flex items-center justify-center font-bold text-sm"
            >
              <RefreshCw size={16} className="ltr:mr-2 rtl:ml-2" /> {t.iot.syncNow}
            </button>
          )}

          {/* Action Anchor */}
          <button
            onClick={dataMode === 'manual' ? async () => { await handleManualSubmit(); runRAGDiagnosis(); } : () => { fetchData(selectedDevice?.id); runRAGDiagnosis(); }}
            disabled={dataMode === 'manual' ? manualLoading : (loading || ragStreaming)}
            className="mt-4 md:mt-0 px-6 py-2 bg-[#C47A2C] hover:bg-[#A36322] text-white font-bold rounded-xl shadow-[inset_0_-2px_4px_rgba(0,0,0,0.1)] transition-transform transform hover:-translate-y-[2px] flex items-center justify-center disabled:opacity-70 disabled:hover:translate-y-0"
          >
            {dataMode === 'manual' ? (
              manualLoading ? <Activity className="animate-spin ltr:mr-2 rtl:ml-2" size={18} /> : <Bot size={18} className="ltr:mr-2 rtl:ml-2" />
            ) : (
              loading ? <Activity className="animate-spin ltr:mr-2 rtl:ml-2" size={18} /> : <Bot size={18} className="ltr:mr-2 rtl:ml-2" />
            )}
            {dataMode === 'manual'
              ? (manualLoading ? t.iot.processing : t.iot.runAnalysis)
              : (loading ? t.iot.scanning : t.iot.runDeepAnalysis)}
          </button>
        </div>

        {/* Global Context Bar */}
        <GlobalContextBar t={t} isRTL={isRTL} onContextChange={handleContextChange} />


        {/* EMPTY STATES: No Devices OR Waiting for Selection */}
        {/* الإدخال اليدوي مش محتاج أجهزة أصلاً، فبنتخطى شاشة "لا توجد أجهزة" في وضع الإدخال اليدوي */}
        {hasCheckedDevices && devices.length === 0 && dataMode === 'live' ? (
          <div className="col-span-12 flex flex-col items-center justify-center py-20 animate-fade-in-slide text-center px-4">
            <div className="p-8 text-center bg-gradient-to-br from-[#1B263B]/5 to-transparent dark:from-white/5 rounded-3xl border border-[#1B263B]/10 dark:border-white/10 max-w-md shadow-lg backdrop-blur-sm">
              <div className="w-16 h-16 mx-auto mb-4 bg-[#C47A2C]/20 rounded-full flex items-center justify-center">
                <Activity className="text-[#C47A2C] w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-[#1B263B] dark:text-white mb-3">
                {t.iot.hardwareIntegrationReq}
              </h3>
              <p className="text-[#1B263B]/70 dark:text-white/70 mb-6 text-sm">
                {t.iot.noRegisteredSensors}
              </p>
            </div>
          </div>
        ) : !selectedDevice && dataMode === 'live' ? (
          <div className="col-span-12 flex flex-col items-center justify-center py-20 animate-fade-in-slide text-center px-4">
            <div className="animate-smooth-float mb-6">
              <div className="w-20 h-20 border-2 border-dashed border-[#1B263B]/20 dark:border-white/20 rounded-full flex items-center justify-center">
                <MapPin size={32} className="text-[#1B263B]/40 dark:text-white/40" />
              </div>
            </div>
            <h3 className="text-lg font-semibold text-[#1B263B]/80 dark:text-white/80">
              {t.iot.awaitingNodeSelection}
            </h3>
            <p className="text-sm text-[#1B263B]/50 dark:text-white/50 mt-2">
              {t.iot.selectNodeDesc}
            </p>
          </div>
        ) : (
          <>
            {/* RAG Diagnostics Panel (replaces old RF banner) */}
            {showDiagnostics && (
              <DiagnosticsPanel
                isStreaming={ragStreaming}
                streamContent={ragContent}
                thinkingContent={ragThinking}
                isThinking={ragIsThinking}
                isComplete={ragComplete}
                hasError={ragError}
                errorMessage={ragErrorMsg}
                onRetry={runRAGDiagnosis}
                onNewAnalysis={() => { setShowDiagnostics(false); setRagContent(''); setRagThinking(''); setRagComplete(false); }}
                t={t}
                isRTL={isRTL}
              />
            )}


            {/* DYNAMIC Sensor Grid (Full Width) */}
            <div className="col-span-12 min-w-0 w-full">
              <div className="overflow-x-auto custom-scrollbar w-full pb-4">
                <div className="flex gap-4 lg:gap-6 w-max">
                  {activeSensors.map((sensor, idx) => {
                    const isLivePulsing = dataMode === 'live' && sensor.key === 'Soil_Moisture' && sensor.displayVal !== 0;
                    return (
                      <div key={idx} className="flex-none w-[260px] lg:w-[320px] h-[340px] xl:h-[380px]">
                        <SensorCard
                          dataMode={dataMode}
                          sensor={sensor}
                          manualInput={manualInputs[sensor.key]}
                          displayVal={sensor.displayVal}
                          isLive={isLivePulsing}
                          disconnectedMsg={t.dashboard.sensorDisconnected}
                          onManualChange={(val) => setManualInputs({ ...manualInputs, [sensor.key]: val === '' ? '' : Number(val) })}
                          theme={theme}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Weather Data Section (Full Width, Horizontal on Desktop) */}
            <div className="col-span-12 flex flex-col mt-6">
              <div className="bg-gradient-to-br from-[#1B263B]/5 to-[#1B263B]/10 dark:from-slate-800 dark:to-slate-800/80 rounded-2xl shadow-sm border border-[#1B263B]/10 dark:border-slate-700 p-6 flex flex-col h-full relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-40 h-40 bg-blue-400/20 dark:bg-blue-500/10 rounded-full blur-3xl -mr-10 -mt-10 pointer-events-none transition-all group-hover:bg-blue-400/30"></div>
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-amber-400/20 dark:bg-amber-500/10 rounded-full blur-3xl -ml-10 -mb-10 pointer-events-none transition-all group-hover:bg-amber-400/30"></div>

                <div className="flex justify-between items-center mb-6 z-10 relative">
                  <div>
                    <h3 className="text-xl font-bold text-[#1B263B] dark:text-white flex items-center gap-2">
                      <Sun className="text-amber-500 animate-[spin_20s_linear_infinite]" size={24} />
                      {t.iot.macroWeather}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 flex items-center gap-1 font-medium">
                      <MapPin size={14} className="text-[#C47A2C]" />
                      {loadingWeather ? t.iot.resolvingLoc : (weatherData?.locationName || t.iot.locationNone)}
                    </p>
                  </div>
                  {loadingWeather && <Activity size={20} className="text-[#6B8F71] animate-spin" />}
                </div>

                {!weatherData && !loadingWeather ? (
                  <div className="flex-1 flex flex-col items-center justify-center opacity-40 z-10 relative py-10">
                    <Cloud size={48} className="mb-2 text-[#1B263B] dark:text-white" />
                    <p className="text-sm font-bold uppercase tracking-wider text-[#1B263B] dark:text-white">{t.iot.awaitingNode}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 flex-1 z-10 relative">
                    {[
                      { label: t.iot.ambientTemp, val: weatherData?.temperature, unit: t?.common?.units?.celsius || '\u00b0C', icon: Thermometer, color: 'text-orange-500', bg: 'bg-orange-500/10' },
                      { label: t.dashboard.hum, val: weatherData?.humidity, unit: t?.common?.units?.percent || '%', icon: Droplets, color: 'text-blue-500', bg: 'bg-blue-500/10' },
                      { label: t.iot.rainfall, val: weatherData?.precipitation, unit: t?.common?.units?.mm || 'mm', icon: CloudRain, color: 'text-cyan-500', bg: 'bg-cyan-500/10' },
                      { label: t.iot.cloudCover, val: weatherData?.clouds, unit: t?.common?.units?.percent || '%', icon: Cloud, color: 'text-slate-500', bg: 'bg-slate-500/10' },
                      { label: t.iot.windSpeed, val: weatherData?.wind, unit: t?.common?.units?.km_h || 'km/h', icon: Wind, color: 'text-teal-500', bg: 'bg-teal-500/10' },
                      { label: t.iot.solarRad, val: weatherData?.solar, unit: t?.common?.units?.W_m2 || 'W/m\u00b2', icon: Sun, color: 'text-amber-500', bg: 'bg-amber-500/10' },
                    ].map((item, i) => (
                      <div key={i} className="bg-white/80 dark:bg-slate-900/60 rounded-xl p-3 border border-white/50 dark:border-slate-700/50 shadow-sm backdrop-blur-md transition-transform transform hover:-translate-y-1 hover:shadow-md flex flex-col justify-between">
                        <div className="flex items-center gap-2 mb-2">
                          <div className={`p-1.5 rounded-lg ${item.bg} ${item.color}`}>
                            <item.icon size={16} />
                          </div>
                          <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">{item.label}</span>
                        </div>
                        <div className="flex items-baseline gap-1.5 mt-auto pt-2">
                          {/* كبرنا الخط لـ 4xl وفي الشاشات الكبيرة 5xl، وضيقنا المسافة بين الأرقام */}
                          <span className={`text-4xl xl:text-5xl font-black ${theme === 'dark' ? item.color : 'text-[#1B263B]'} tracking-tighter drop-shadow-sm transition-colors`}>
                            {loadingWeather ? '--' : (item.val !== undefined ? item.val : '--')}
                          </span>
                          {/* كبرنا وحدة القياس شوية عشان تتناسب مع الرقم الكبير */}
                          <span className="text-sm xl:text-base text-gray-500 dark:text-gray-400 font-bold mb-1.5 opacity-80">{item.unit}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Bottom Section: Historical Telemetry - DYNAMIC Grid */}
            <div className="col-span-12 min-w-0 mt-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-[#1B263B] dark:text-white flex items-center gap-2">
                  <Activity className="text-[#C47A2C]" />
                  {t.iot.historicalTelem}
                </h3>
                <select
                  value={historyInterval}
                  onChange={(e) => setHistoryInterval(e.target.value)}
                  className="bg-white dark:bg-slate-800 border border-[#1B263B]/10 dark:border-slate-700 rounded-xl px-3 py-1.5 text-sm font-medium shadow-sm outline-none text-[#1B263B] dark:text-white"
                >
                  <option value="Live">{t.iot.liveRaw}</option>
                  <option value="Hours">{t.iot.hours}</option>
                  <option value="Days">{t.iot.days}</option>
                  <option value="Weeks">{t.iot.weeks}</option>
                  <option value="Months">{t.iot.months}</option>
                  <option value="Years">{t.iot.years}</option>
                </select>
              </div>

              <div className="overflow-x-auto custom-scrollbar w-full pb-4">
                <div className="flex gap-6 w-max">
                  {activeSensors.map((sensor, idx) => {
                    const themeColor = getSensorThemeColor(sensor.key);
                    return (
                      <div key={`chart-${idx}`} className="flex-none w-[280px] lg:w-[380px] bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-[#1B263B]/5 dark:border-slate-700 p-5 flex flex-col transition-colors group">

                        <div className="flex justify-between items-center mb-4">
                          <div>
                            <h4 className="text-sm font-bold text-[#1B263B] dark:text-white">{t.dashboard[SENSOR_MAP[sensor.key]] || sensor.label}</h4>
                          </div>
                          <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: `${themeColor}20`, color: themeColor }}>
                            {sensor.icon && <sensor.icon className="w-4 h-4 animate-smooth-float group-hover:scale-125 transition-transform" strokeWidth={1.5} />}
                          </div>
                        </div>

                        <div className="flex-1 w-full h-[150px]" style={{ direction: 'ltr', position: 'relative' }}>
                          {loadingHistory ? (
                            <div className="flex w-full h-full justify-center items-center">
                              <div className="text-[#6B8F71] animate-pulse font-medium text-sm">{t.iot.scanningArray}</div>
                            </div>
                          ) : deviceHistory.length > 0 ? (
                            <div style={{ width: '100%', height: 130, position: 'relative' }}>
                              <ResponsiveContainer width="100%" height="100%">
                                {(() => {
                                  const { domain, ticks } = getChartScale(sensor, deviceHistory);
                                  return (
                                    <AreaChart data={deviceHistory} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>

                                      <defs>
                                        <linearGradient id={`color-${sensor.historyKey}`} x1="0" y1="0" x2="0" y2="1">
                                          <stop offset="5%" stopColor={themeColor} stopOpacity={0.6} />
                                          <stop offset="95%" stopColor={themeColor} stopOpacity={0.0} />
                                        </linearGradient>
                                      </defs>

                                      <CartesianGrid horizontal={false} vertical={false} />
                                      <XAxis
                                        dataKey="timeLabel"
                                        tick={{ fontSize: 10, fill: theme === 'dark' ? '#64748b' : '#94a3b8', fontWeight: 600 }}
                                        axisLine={false}
                                        tickLine={false}
                                        minTickGap={20}
                                      />

                                      <YAxis
                                        domain={['auto', 'auto']}
                                        tick={{ fontSize: 10, fill: theme === 'dark' ? '#64748b' : '#94a3b8', fontWeight: 600 }}
                                        axisLine={false}
                                        tickLine={false}
                                        width={35}
                                        tickFormatter={(value) => Number(value).toFixed(1)}
                                      />

                                      <Tooltip
                                        isAnimationActive={false}
                                        formatter={(value) => Number(value).toFixed(2)}
                                        contentStyle={{ backgroundColor: theme === 'dark' ? '#1e293b' : '#1B263B', borderColor: theme === 'dark' ? '#334155' : '#1B263B', color: '#fff', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', fontSize: '12px', padding: '6px 12px' }}
                                        itemStyle={{ fontWeight: 'bold' }}
                                        labelStyle={{ color: '#94a3b8', marginBottom: '2px', fontSize: '10px' }}
                                        cursor={{ stroke: theme === 'dark' ? '#334155' : '#e2e8f0', strokeWidth: 1, strokeDasharray: '3 3' }}
                                      />

                                      <Area
                                        type="monotone"
                                        connectNulls
                                        name={sensor.label}
                                        dataKey={sensor.historyKey}
                                        stroke={themeColor}
                                        strokeWidth={3}
                                        fill={`url(#color-${sensor.historyKey})`}
                                        activeDot={{ r: 5, fill: theme === 'dark' ? '#1e293b' : '#ffffff', strokeWidth: 2, stroke: themeColor }}
                                      />
                                    </AreaChart>
                                  );
                                })()}
                              </ResponsiveContainer>
                            </div>
                          ) : (
                            <div className="flex w-full h-full justify-center items-center">
                              <p className="text-gray-400 text-xs font-medium">{t.iot.noHistory}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>

            {/* Macro Weather Drill-Down Charts */}
            {selectedDevice && (
              <div className="col-span-12 min-w-0 mt-6">
                <MacroWeatherChart
                  deviceId={selectedDevice.id}
                  theme={theme}
                  t={t}
                />
              </div>
            )}

            {/* Standalone Section: Satellite IoT Mapping */}
            <div className="col-span-12 min-w-0 mt-6">
              <IoTMap devices={devices} theme={theme} t={t} />
            </div>
          </>
        )}
      </div>
    </div >
  );
};

export default IoTView;